<template>
  <b-modal ref="modal" :id="modalId" centered hide-footer title="Coaching Detail" size="xl" hide-header-close>
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading training detail...</p>
    </div>

    <div v-else-if="data" class="d-flex flex-column gap-2">
      <b-row>
        <b-col class="col-6 d-flex flex-column gap-3 justify-content-between">
          <div class="d-flex flex-column gap-3">
            <div>
              <span :class="getTrainingStatusClass(data.status)">
                {{ snakeToTitle(data.status) }}
              </span>
            </div>
            <div class="d-flex flex-column gap-3">
              <h2 class="fw-bold mb-1">{{ data.coaching.title }}</h2>
              <div class="d-flex flex-row gap-2 align-items-center">
                <span
                  v-if="data.coaching.coaching_type_name"
                  :class="['badge-custom', badgeColorClass(data?.coaching?.coaching_type_id)]"
                >
                  {{ data.coaching.coaching_type_name || "-" }}
                </span>
                <span
                  class="badge-custom"
                  :style="{
                    backgroundColor: 'transparent',
                    border: `1px solid black`,
                    color: 'black',
                  }"
                >
                  {{ data.competency.name }}
                </span>
              </div>
            </div>
            <div>
              <i class="fe fe-user me-2"></i>
              <span>{{ data.coaching.coach_name || "-" }}</span>
            </div>
          </div>
          <div>
            <h4>
              <p class="fw-light">Methodology</p>
              <p>
                <i class="fe fe-book"></i>
                {{ data.coaching.methodology || "No Methodology available." }}
              </p>
            </h4>
          </div>
        </b-col>
        <b-col class="col-6 d-flex align-items-center justify-content-center">
          <b-img
            :src="data?.coaching?.cover_image || 'https://dummyimage.com/400x200/6c757d/fff&text=Coaching+Thumbnail'"
            fluid
            :alt="data?.coaching.title"
            class="rounded mb-3"
            style="max-height: 250px; object-fit: cover"
          />
        </b-col>
      </b-row>

      <b-row class="d-flex flex-column gap-2">
        <b-col class="d-flex flex-row justify-content-between align-content-end">
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#schedules"> Schedules </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#presentation"> Presentation </a>
            </li>
          </ul>
          <div class="d-flex align-items-center">
            <button v-if="data.status === 'approved'" type="button" class="btn btn-danger" @click="openAddScheduleModal()">
              Add Schedules
            </button>
          </div>
        </b-col>

        <div class="tab-content">
          <div id="schedules" class="tab-pane fade show active">
            <vue-good-table
              :columns="columns"
              :rows="data.coaching_schedules"
              style-class="vgt-table border-0 table-auto"
              styleClass="vgt-table condensed"
            >
              <div slot="emptystate" class="text-center font-size-14">No data available</div>

              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'action'" class="d-flex gap-3 justify-content-center align-items-center">
                  <button
                    v-if="props.row.coaching_summary.status !== 'completed'"
                    class="btn btn-sm btn-warning"
                    v-b-tooltip.hover.left="'Update Summary'"
                    @click="updateSummary(props.row)"
                  >
                    <span class="fe fe-edit"></span>
                  </button>
                </span>

                <span v-else-if="props.column.field === 'date'">
                  <div class="d-flex flex-row align-items-center justify-content-start">
                    <span>{{ formatDate(props.row.date) }}</span>
                  </div>
                </span>

                <span v-else-if="props.column.field === 'start_time'">
                  <div class="d-flex flex-row align-items-center justify-content-start">
                    <span>{{ formatTimeToHHMM(props.row.start_time) }}</span>
                  </div>
                </span>

                <span v-else-if="props.column.field === 'end_time'">
                  <div class="d-flex flex-row align-items-center justify-content-start">
                    <span>{{ formatTimeToHHMM(props.row.end_time) }}</span>
                  </div>
                </span>

                <span v-else-if="props.column.field === 'coaching_summary.attachments'">
                  <div class="d-flex flex-row align-items-center justify-content-start">
                    <a
                      v-if="props.row.coaching_summary.attachments"
                      :href="props.row.coaching_summary.attachments"
                      target="_blank"
                      >File</a
                    >
                    <span v-else>No attachments available</span>
                  </div>
                </span>

                <span v-else-if="props.column.field === 'coaching_summary.status'">
                  <div class="d-flex flex-row align-items-center justify-content-start">
                    <span :class="getTrainingStatusClass(props.row.coaching_summary.status)">
                      {{ snakeToTitle(props.row.coaching_summary.status) }}
                    </span>
                  </div>
                </span>

                <span v-else class="d-flex align-items-center justify-content-start">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>

          <div id="presentation" class="tab-pane fade">
            <div v-if="data?.presentation_document" class="row">
              <div class="col-md-8">
                <div class="d-flex gap-3 flex-column">
                  <div
                    v-if="isPdfPresentation(data?.presentation_document)"
                    class="course-image-container rounded"
                    style="cursor: pointer"
                    @click="previewPresentation"
                  >
                    <div class="pdf-overlay">
                      <div class="pdf-icon-container-large">
                        <i class="fe fe-file-text"></i>
                      </div>
                      <p class="pdf-label mt-3 mb-1">PDF Presentation</p>
                      <small class="text-muted">Click to view</small>
                    </div>
                  </div>
                  <div v-else class="course-image-container rounded" style="cursor: pointer" @click="previewPresentation">
                    <img
                      :src="data?.presentation_document"
                      alt="Document"
                      class="course-image"
                      @error="handleCertImageError"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import {
  formatCurrency,
  formatLongDate,
  formatTime,
  modifyStatus,
  snakeToTitle,
  getTrainingStatusClass,
  formatTimeToHHMM,
  formatDate,
} from "../utils/utils";

export default {
  name: "SocialLearningModal",

  components: {},

  props: {
    modalId: { type: String, default: "social-learning-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      columns: [
        {
          label: "Date",
          field: "date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Start Time",
          field: "start_time",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "End Time",
          field: "end_time",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Summary",
          field: "coaching_summary.summary",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Feedback",
          field: "coaching_summary.feedback",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Attachment",
          field: "coaching_summary.attachments",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "coaching_summary.status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  computed: {},

  methods: {
    formatCurrency,
    snakeToTitle,
    getTrainingStatusClass,
    formatLongDate,
    formatTime,
    formatDate,
    modifyStatus,
    formatTimeToHHMM,
    badgeColorClass(type) {
      if (type === "M") return "badge-blue";
      return "badge-green";
    },
    updateSummary(data) {
      this.$emit("open-update-summary-modal", data);
    },

    openAddScheduleModal() {
      this.$emit("open-schedule-modal");
    },

    handleImageError(event) {
      event.target.src = "https://dummyimage.com/300x200/FAFAFA/000000&text=Course";
    },

    handleCertImageError(event) {
      event.target.src = "https://dummyimage.com/300x200/FAFAFA/000000&text=Presentation";
    },

    isPdfPresentation(url) {
      if (typeof url !== "string") return false;

      const cleanUrl = url.split("?")[0].toLowerCase();

      return cleanUrl.endsWith(".pdf");
    },

    previewPresentation() {
      if (this.data?.presentation_document) {
        const certUrl = `${this.data?.presentation_document}`;
        window.open(certUrl, "_blank");
      }
    },
  },
};
</script>

<style scoped>
.w-50 {
  width: 50%;
}

.badge-custom {
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.35rem;
}

.alert-heading {
  margin-bottom: 0.5rem;
}

:deep(.table-header) {
  font-size: 11px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 11px !important;
}

.course-image-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  background-color: #fafafa;
}

.course-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pdf-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
}

.pdf-thumbnail {
  cursor: pointer;
  padding: 2rem;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container {
  width: 80px;
  height: 100px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-label {
  font-weight: 600;
  color: #1a202c;
  font-size: 1rem;
}

.pdf-thumbnail-full {
  cursor: pointer;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail-full:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container-large {
  width: 100px;
  height: 130px;
  margin: 0 auto;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container-large i {
  font-size: 3.5rem;
  color: #fff;
}
</style>
